import { useEffect, useState } from 'react';
import { Contador } from '../components/ProductosSeccion';
import { Alerta, ErrorApi, Guia, Pill } from '../components/ui';
import type { EstadoProductoCr, FacturacionCrApi, ProductosCr } from './types';

const ETIQUETA: Record<EstadoProductoCr, { texto: string; tono: 'ok' | 'pendiente' | 'error' }> = {
  SIN_CODIGO: { texto: 'Sin código', tono: 'error' },
  NO_EN_CATALOGO: { texto: 'Código no válido', tono: 'pendiente' },
  OK: { texto: 'Listo', tono: 'ok' },
};

export function ProductosCR({ api, idEmpresa }: { api: FacturacionCrApi; idEmpresa: number }) {
  const [datos, setDatos] = useState<ProductosCr | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [filtro, setFiltro] = useState<EstadoProductoCr | ''>('');
  const [buscar, setBuscar] = useState('');
  const [aplicado, setAplicado] = useState('');

  useEffect(() => {
    let vivo = true;
    setError(null);
    api.productos(idEmpresa, filtro, aplicado).then((d) => vivo && setDatos(d)).catch((e) => vivo && setError(e));
    return () => {
      vivo = false;
    };
  }, [api, idEmpresa, filtro, aplicado]);

  return (
    <section className="seccion">
      <Guia
        id="cr-productos"
        titulo="Cómo preparar sus productos"
        pasos={[
          <>Cada producto debe tener su <b>código CABYS</b> (13 dígitos) asignado en el sistema donde administra sus productos.</>,
          <>Revise abajo los productos <b>Sin código</b> o con <b>Código no válido</b> y corríjalos en ese sistema.</>,
          <>La columna IVA muestra la tarifa que tiene ese código en el catálogo: verifique que coincida con lo que cobra.</>,
          <>Vuelva a esta pantalla para confirmar que todos quedaron en <b>Listo</b>.</>,
        ]}
        nota="Un producto sin código CABYS válido queda fuera del comprobante y Hacienda puede rechazarlo. Si eso pasó, corrija el código y use Emitir de nuevo en Documentos."
      />
      <header className="seccion__cab">
        <div>
          <h2>Productos y código CABYS</h2>
          <p className="texto-sec">Revisión de solo lectura. Los productos se editan en su sistema de productos.</p>
        </div>
      </header>
      <ErrorApi error={error} />
      {datos && (
        <>
          <div className="contadores">
            <Contador valor={datos.total} texto="Productos" activo={filtro === ''} onClick={() => setFiltro('')} />
            <Contador valor={datos.sinCodigo} texto="Sin código" tono="error" activo={filtro === 'SIN_CODIGO'} onClick={() => setFiltro('SIN_CODIGO')} />
            <Contador valor={datos.noEnCatalogo} texto="Código no válido" tono="pendiente" activo={filtro === 'NO_EN_CATALOGO'} onClick={() => setFiltro('NO_EN_CATALOGO')} />
            <Contador valor={datos.listos} texto="Listos" tono="ok" activo={filtro === 'OK'} onClick={() => setFiltro('OK')} />
          </div>
          {datos.sinCodigo + datos.noEnCatalogo === 0 && datos.total > 0 && <Alerta tipo="exito" titulo="Todos sus productos tienen un código CABYS válido." />}
          <form className="filtros" onSubmit={(e) => { e.preventDefault(); setAplicado(buscar.trim()); }}>
            <input placeholder="Buscar por nombre o código de producto" value={buscar} onChange={(e) => setBuscar(e.target.value)} aria-label="Buscar producto" />
            <button className="btn btn--secundario" type="submit">Buscar</button>
          </form>
          {datos.items.length === 0 ? (
            <div className="vacio">No hay productos con este filtro.</div>
          ) : (
            <div className="tabla-scroll">
              <table className="tabla">
                <thead>
                  <tr><th>Producto</th><th>Código CABYS</th><th>Descripción en el catálogo</th><th className="num">IVA</th><th>Estado</th></tr>
                </thead>
                <tbody>
                  {datos.items.map((x) => (
                    <tr key={x.id}>
                      <td>{x.descripcion ?? '—'} <span className="mono texto-sec">#{x.id}</span></td>
                      <td className="mono">{x.codigo ?? '—'}</td>
                      <td>{x.descripcionCabys ?? '—'}</td>
                      <td className="num">{x.impuesto === null ? '—' : `${x.impuesto} %`}</td>
                      <td><Pill tono={ETIQUETA[x.estado].tono}>{ETIQUETA[x.estado].texto}</Pill></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {datos.items.length >= 500 && <p className="texto-sec">Se muestran los primeros 500. Use el buscador para encontrar otros.</p>}
            </div>
          )}
        </>
      )}
      {!datos && !error && <div className="cargando">Cargando productos…</div>}
    </section>
  );
}
